import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";

export default function FieldEditor({ field, onCancel, onSave }) {
  const [label, setLabel] = useState(field.label || "");
  const [placeholder, setPlaceholder] = useState(field.placeholder || "");
  const [content, setContent] = useState(field.content || "");
  const [required, setRequired] = useState(!!field.required);
  const [options, setOptions] = useState(
    field.options?.length ? [...field.options] : ["Option 1"]
  );

  useEffect(() => {
    setLabel(field.label || "");
    setPlaceholder(field.placeholder || "");
    setContent(field.content || "");
    setRequired(!!field.required);
    setOptions(field.options?.length ? [...field.options] : ["Option 1"]);
  }, [field]);

  const isContent = field.type === "h2" || field.type === "body";
  const hasOptions = field.type === "dropdown" || field.type === "checkbox";
  const hasPlaceholder = field.type === "text";
  const showRequired = !isContent;

  const handleSave = () => {
    const changes = { label: label.trim() || field.label };
    if (isContent) changes.content = content;
    if (showRequired) changes.required = required;
    if (hasPlaceholder) changes.placeholder = placeholder;
    if (hasOptions) {
      changes.options = options.map((o) => o.trim()).filter(Boolean).slice(0, 20);
      if (!changes.options.length) changes.options = ["Option 1"];
    }
    onSave(changes);
  };

  return (
    <div className="rounded-xl border border-dashed border-blue-300 bg-white p-4 shadow-sm">
      <p className="mb-3 text-left text-xs font-medium text-blue-500">Edit field</p>

      <label className="mb-1 block text-left text-xs text-gray-500">
        {isContent ? "Text" : "Field Label"}
      </label>
      <input
        value={isContent ? content : label}
        onChange={(e) =>
          isContent ? setContent(e.target.value) : setLabel(e.target.value)
        }
        className="mb-3 w-full rounded-lg bg-gray-50 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-100"
      />

      {hasPlaceholder && (
        <>
          <label className="mb-1 block text-left text-xs text-gray-500">
            Placeholder Text
          </label>
          <input
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            className="mb-3 w-full rounded-lg bg-gray-50 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-100"
          />
        </>
      )}

      {hasOptions && (
        <div className="mb-3">
          <label className="mb-1 block text-left text-xs text-gray-500">Options</label>
          <div className="max-h-40 space-y-2 overflow-y-auto pr-1">
            {options.map((opt, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  value={opt}
                  onChange={(e) => {
                    const next = [...options];
                    next[idx] = e.target.value;
                    setOptions(next);
                  }}
                  className="flex-1 rounded-lg bg-gray-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-100"
                />
                <button
                  type="button"
                  onClick={() => setOptions(options.filter((_, i) => i !== idx))}
                  className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOptions([...options, `Option ${options.length + 1}`])}
            className="mt-2 text-left text-sm font-medium text-blue-500"
          >
            + Add Option
          </button>
        </div>
      )}

      {showRequired && (
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm text-gray-700">Required field</span>
          <button
            type="button"
            role="switch"
            aria-checked={required}
            onClick={() => setRequired((v) => !v)}
            className={`relative h-6 w-11 rounded-full transition ${
              required ? "bg-blue-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
                required ? "left-5" : "left-0.5"
              }`}
            />
          </button>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="rounded-lg bg-[#EAF524] px-4 py-2 text-sm font-semibold text-black"
        >
          Save
        </button>
      </div>
    </div>
  );
}
